import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cognitoAuthService } from '../services/cognitoAuth';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const LoginPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = t('validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('validation.emailInvalid');
    }

    if (!formData.password) {
      errors.password = t('validation.passwordRequired');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const loginData = {
        email: formData.email,
        password: formData.password,
      };

      const result = await cognitoAuthService.login(loginData);

      if (result.success) {
        // Store session info if needed
        console.log('Login successful');
        window.location.href = '/tracks';
      } else {
        setError(result.message || t('messages.loginError'));
      }
    } catch (err) {
      console.error('Email login failed:', err);
      setError(t('messages.loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    try {
      cognitoAuthService.loginWithGoogle();
    } catch (err) {
      console.error('Google login failed:', err);
      setError(t('messages.googleError'));
    }
  };

  const handleFacebookLogin = () => {
    try {
      cognitoAuthService.loginWithFacebook();
    } catch (err) {
      console.error('Facebook login failed:', err);
      setError(t('messages.facebookError'));
    }
  };

  if (isLoading) {
    return (
      <div
        className='min-h-screen flex items-center justify-center'
        style={{ background: 'rgb(8 6 20 / 100%)' }}
      >
        <div className='text-center animate-fade-in'>
          <div className='spinner mx-auto mb-6 glow'></div>
          <p className='text-base' style={{ color: 'var(--text-secondary)' }}>
            {t('login.loggingIn')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: 'rgb(8 6 20 / 100%)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SEO
        title={tCommon('seo.login.title')}
        canonical='/login'
        noIndex={true}
      />
      <Header variant='default' />
      <div
        className='flex items-center justify-center background-login'
        style={{ paddingTop: '80px', flex: 1 }}
      >
        <div className='login-background-overlay'></div>
        <div
          className='max-w-md w-full space-y-4 md:space-y-8 p-4 md:p-8 animate-fade-in'
          style={{
            zIndex: 2,
          }}
        >
          <div className='dark-card'>
            <div className='text-center'>
              <h2
                className='mb-2 text-2xl sm:text-3xl md:text-4xl font-semibold'
                style={{
                  color: 'var(--text-primary)',
                  fontFamily: 'Lexend Deca, sans-serif',
                }}
              >
                {t('login.title')}
              </h2>
            </div>
            {error && (
              <div
                className='mb-6 p-4 border rounded-2xl animate-fade-in'
                style={{
                  background: 'var(--glass-bg)',
                  borderColor: 'var(--error)',
                  color: 'var(--error)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                {error}
              </div>
            )}

            <form onSubmit={handleEmailLogin} className='space-y-4'>
              <div className='animate-slide-right stagger-1'>
                <label
                  htmlFor='email'
                  className='block text-sm font-medium text-white mb-2'
                >
                  {t('login.email')}
                </label>
                <input
                  id='email'
                  name='email'
                  type='email'
                  value={formData.email}
                  onChange={handleInputChange}
                  className='input'
                  style={{
                    borderColor: formErrors.email ? 'var(--error)' : undefined,
                  }}
                  placeholder={t('login.emailPlaceholder')}
                />
                {formErrors.email && (
                  <p
                    className='mt-2 text-sm animate-fade-in'
                    style={{ color: 'var(--error)' }}
                  >
                    {formErrors.email}
                  </p>
                )}
              </div>

              <div className='animate-slide-right stagger-2'>
                <label
                  htmlFor='password'
                  className='block text-sm font-medium text-white mb-2'
                >
                  {t('login.password')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id='password'
                    name='password'
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    className='input'
                    style={{
                      borderColor: formErrors.password
                        ? 'var(--error)'
                        : undefined,
                      paddingRight: '2.75rem',
                    }}
                    placeholder={t('login.passwordPlaceholder')}
                  />
                  <button
                    type='button'
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-secondary)',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21' />
                      </svg>
                    ) : (
                      <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                        <path strokeLinecap='round' strokeLinejoin='round' d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                      </svg>
                    )}
                  </button>
                </div>
                {formErrors.password && (
                  <p
                    className='mt-2 text-sm animate-fade-in'
                    style={{ color: 'var(--error)' }}
                  >
                    {formErrors.password}
                  </p>
                )}
              </div>

              <div className='flex items-center justify-end animate-slide-right stagger-3 text-sm'>
                <Link
                  to='/forgot-password'
                  className='font-medium text-white-600'
                  style={{ margin: '0 auto 12px auto' }}
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>

              <div className='flex w-full justify-center animate-slide-right stagger-4'>
                <button
                  type='submit'
                  disabled={isLoading}
                  className='btn btn-primary'
                >
                  {t('login.submit')}
                </button>
              </div>
            </form>

            <div className='space-y-4 mt-8 text-center'>
              <div className='divider'></div>

              <button
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className='btn btn-primary'
              >
                <svg className='w-5 h-5 mr-2' viewBox='0 0 24 24'>
                  <path
                    fill='currentColor'
                    d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                  />
                  <path
                    fill='currentColor'
                    d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                  />
                  <path
                    fill='currentColor'
                    d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                  />
                  <path
                    fill='currentColor'
                    d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                  />
                </svg>
                {t('login.continueWithGoogle')}
              </button>

              <button
                onClick={handleFacebookLogin}
                disabled={isLoading}
                className='btn btn-secondary'
              >
                <svg
                  className='w-5 h-5 mr-2'
                  fill='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
                </svg>
                {t('login.continueWithFacebook')}
              </button>
            </div>

            <div className='mt-8 text-center animate-fade-in stagger-4'>
              <div className='divider'></div>

              <p className=' text-sm' style={{ color: '#fff' }}>
                {t('login.noAccount')}{' '}
                <Link
                  to='/register'
                  className='font-medium text-white-600 transition-colors duration-200 hover:underline'
                >
                  {t('login.registerHere')}
                </Link>
              </p>

              <p className='mt-6 text-sm leading-relaxed text-white'>
                {t('login.termsAgreement')}{' '}
                <a href='/terms' className='text-white-600 '>
                  {t('login.termsOfService')}
                </a>{' '}
                {t('login.and')}{' '}
                <a href='/privacy' className='text-white-600'>
                  {t('login.privacyPolicy')}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage;
